import { parseDbTimestamp } from "@repo/shared";
import type { ClientNote } from "@repo/shared/types";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LexicalMarkdownEditor } from "@/components/ui/markdown-editor-lexical";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useConfirm } from "@/hooks/use-confirm";
import { patch, post } from "@/lib/api/_client";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./note-editor.i18n";

type SaveStatus = "idle" | "saving" | "saved" | "error";

export type NoteEditorProps = {
  /** `null` creates a new note; the first successful save turns it into an update. */
  note: ClientNote | null;
  clientId: string;
  onDelete?: (noteId: string) => void;
  /** Back / done — the parent returns to its list and refetches. */
  onCancel: () => void;
};

// The full-page editor for a single note (QL-125: the list that used to wrap
// it lives in notes-and-files-section.tsx, shared with calls, meetings and files).
export function NoteEditor({ note, clientId, onDelete, onCancel }: NoteEditorProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [title, setTitle] = useState(note?.title || "");
  const [content, setContent] = useState(note?.content || "");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const { confirm, ConfirmationDialog } = useConfirm();

  // Track saved state
  const savedNoteId = useRef<string | null>(note?.id || null);
  const lastSavedTitle = useRef(note?.title || "");
  const lastSavedContent = useRef(note?.content || "");
  // The save currently on the wire, if any. The 10 s autosave and a manual
  // Save can overlap while the create POST is still in flight; without this
  // both would see no saved id and create the note twice.
  const inFlight = useRef<Promise<boolean> | null>(null);

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    title !== lastSavedTitle.current || content !== lastSavedContent.current;

  // Debounced values for autosave
  const debouncedTitle = useDebouncedValue(title, 10000);
  const debouncedContent = useDebouncedValue(content, 10000);

  // Save function (used by both autosave and manual save)
  const performSave = useCallback(
    async (titleToSave: string, contentToSave: string): Promise<boolean> => {
      if (!titleToSave.trim()) return false;
      // Let a running save finish first: it may assign the id and it may
      // already have written exactly this content.
      if (inFlight.current) await inFlight.current;
      if (titleToSave === lastSavedTitle.current && contentToSave === lastSavedContent.current) {
        return true;
      }

      setSaveStatus("saving");
      setError(null);

      const run = (async () => {
        try {
          if (savedNoteId.current) {
            await patch(`/internal/client-notes/${clientId}/${savedNoteId.current}`, {
              title: titleToSave,
              content: contentToSave,
            });
          } else {
            const data = await post<ClientNote>(`/internal/client-notes/${clientId}`, {
              title: titleToSave,
              content: contentToSave,
            });
            savedNoteId.current = data.id;
          }
          lastSavedTitle.current = titleToSave;
          lastSavedContent.current = contentToSave;
          setSaveStatus("saved");
          return true;
        } catch {
          setSaveStatus("error");
          // Judge by what is on the wire, not by how the editor was opened: an
          // autosave may already have created the note.
          setError(savedNoteId.current ? t.failedToUpdate : t.failedToCreate);
          return false;
        }
      })();
      inFlight.current = run;
      try {
        return await run;
      } finally {
        if (inFlight.current === run) inFlight.current = null;
      }
    },
    [clientId, t],
  );

  // Autosave effect
  useEffect(() => {
    startTransition(() => {
      performSave(debouncedTitle, debouncedContent);
    });
  }, [debouncedTitle, debouncedContent, performSave]);

  // Manual save handler
  const handleManualSave = async () => {
    if (!title.trim()) {
      setError(t.titleRequired);
      return;
    }
    const ok = await performSave(title, content);
    if (ok) onCancel();
  };

  // Reset saved status after a delay
  useEffect(() => {
    if (saveStatus === "saved") {
      const timer = setTimeout(() => setSaveStatus("idle"), 2000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);

  const handleDelete = async () => {
    if (!savedNoteId.current || !onDelete) return;

    const confirmed = await confirm({
      title: t.deleteNote,
      description: t.deleteConfirmation(title || t.newNote),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (confirmed) {
      onDelete(savedNoteId.current);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <>
      <div className="space-y-4">
        {/* Back link */}
        <Button variant="ghost" size="sm" className="-ml-2" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t.backToNotes}
        </Button>

        <div className="border rounded-lg p-4 bg-muted/30">
          <div className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="title" className="sr-only">
                  {t.title}
                </Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t.enterNoteTitle}
                  className="font-medium text-base bg-transparent px-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 focus:border-solid focus:border-primary"
                />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Save status indicator */}
                <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-[70px] justify-end">
                  {saveStatus === "saving" && (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>{t.saving}</span>
                    </>
                  )}
                  {saveStatus === "saved" && (
                    <>
                      <Check className="h-3 w-3" />
                      <span>{t.saved}</span>
                    </>
                  )}
                </div>

                {/* Manual save button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualSave}
                  disabled={
                    isPending || !title.trim() || !hasUnsavedChanges || saveStatus === "saving"
                  }
                >
                  {t.save}
                </Button>

                {/* Delete button */}
                {savedNoteId.current && onDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    className="text-destructive hover:text-destructive"
                    disabled={isPending}
                  >
                    {t.delete}
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content" className="sr-only">
                {t.content}
              </Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <LexicalMarkdownEditor
                  value={content}
                  onChange={setContent}
                  placeholder={t.writeNoteHere}
                  showSpeechToText={true}
                  showAI={false}
                  className="min-h-[200px]"
                />
              </div>
            </div>

            {note && (
              <div className="text-xs text-muted-foreground">
                {note.createdBy?.name} &bull;{" "}
                <TimeAgoTooltip date={parseDbTimestamp(note.updatedAt)}>
                  {getRelativeTime(note.updatedAt, userLanguage)}
                </TimeAgoTooltip>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmationDialog />
    </>
  );
}
